import { AccountHeader } from "@/components/AccountHeader";
import { AppFooter } from "@/components/AppFooter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Calendar, MapPin, CreditCard, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getOrders } from "@/services/order.service";
import { useToast } from "@/hooks/use-toast";
import { API_CONFIG } from "@/config/api";

const Orders = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    fetchOrders();
  }, [isAuthenticated, navigate, user?.id]);

  const fetchOrders = async (pageToLoad = 1) => {
    if (!user?.id) return;

    try {
      setIsLoadingOrders(true);
      const { orders: ordersData, meta } = await getOrders(
        user.id,
        pageToLoad,
        perPage
      );
      setOrders(ordersData);
      setTotalItems(meta?.total ?? ordersData?.length);
      setTotalPages(
        meta?.last_page ??
          Math.max(1, Math.ceil((meta?.total ?? ordersData?.length) / perPage))
      );
      setPage(meta?.current_page ?? pageToLoad);
      console.log("Fetched orders:", ordersData, meta);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load orders. Please try again later.",
      });
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchOrders(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, page]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
    // fetchOrders will be triggered by useEffect
  };

  const getStatusColor = (status: string) => {
    // return hex color for banner text based on exact status
    const s = status || "";
    if (s === "processing") return "#9E510B";
    if (s === "completed") return "#138A43";
    if (s === "closed") return "#2158BF";
    if (s === "canceled" || s === "canceled") return "#CB3939";
    // default
    return "#fff";
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AccountHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">My Orders</h1>
          <p className="text-muted-foreground mt-2">
            View and track your orders
          </p>
        </div>

        {isLoadingOrders ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No orders yet</h3>
              <p className="text-muted-foreground mb-4">
                You haven't placed any orders yet.
              </p>
              <Button onClick={() => navigate("/")}>Start Shopping</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="overflow-hidden">
                {/* Top meta bar */}
                <div className="bg-gray-100 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start md:items-center gap-6">
                    <div>
                      <div className="text-xs text-muted-foreground uppercase">
                        Order placed
                      </div>
                      <div className="font-medium">
                        {formatDate(order.created_at)}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-muted-foreground uppercase">
                        Total
                      </div>
                      <div className="font-medium text-lg">
                        ₹
                        {parseFloat(order.grand_total || "0").toLocaleString(
                          "en-IN",
                          { minimumFractionDigits: 2 }
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-muted-foreground uppercase">
                        Ship to
                      </div>
                      <div className="text-sm text-primary font-medium">
                        {order.shipping_address?.first_name}{" "}
                        {order.shipping_address?.last_name}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm font-medium">Order #{order.id}</div>
                    <div className="mt-1 flex items-center justify-end gap-4 text-sm">
                      <button
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="text-primary hover:underline"
                      >
                        View order details
                      </button>
                      {order.status === "completed" ? (
                        <button className="text-primary hover:underline">
                          Invoice ▾
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Delivery status large banner */}
                <div className="px-6 py-6">
                  {(order.status || "").toLowerCase() === "pending" ? (
                    <div className="pt-3">
                      <div style={{ fontSize: 18, fontWeight: 600 }}>
                        Status <span style={{ color: "#CB3939" }}>Pending</span>
                      </div>
                      <div className="text-sm font-bold">Not Yet Shipped</div>
                    </div>
                  ) : null}
                  <h3
                    className="text-3xl font-bold"
                    style={{
                      color: getStatusColor(order.status),
                    }}
                  >
                    {order.status === "canceled"
                      ? "Cancelled "
                      : order.status === "closed"
                      ? "Returned requested "
                      : order.status === "completed"
                      ? "Delivered"
                      : "In Progress"}{" "}
                  </h3>
                </div>

                <CardContent className="px-6 pb-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    {/* Left: product list (show first item prominently) */}
                    <div className="flex-1">
                      {order.items && order.items.length > 0 ? (
                        order.items.map((item, idx) => {
                          console.log(item);
                          return (
                            <div
                              key={item.id || idx}
                              className="flex items-start gap-4 py-4 border-b last:border-b-0"
                            >
                              <img
                                src={
                                  item?.product?.base_image?.path === undefined
                                    ? item?.product?.base_image?.large_image_url
                                    : API_CONFIG.s3BucketUrl +
                                      item?.product?.base_image?.path
                                }
                                alt={item?.product?.name || "Product"}
                                className="h-20 w-20 object-contain rounded-md bg-white border"
                              />
                              <div className="flex-1">
                                <a
                                  className="text-lg text-blue-700 font-medium hover:underline"
                                  onClick={() =>
                                    navigate(
                                      `/product/${
                                        item.product_id || item.product_sku
                                      }`
                                    )
                                  }
                                >
                                  {item?.product?.name || "Product"}
                                </a>
                                <div className="text-sm text-muted-foreground mt-1">
                                  Qty: {item.qty_ordered}
                                </div>
                              </div>
                              <div className="text-right">
                                {/* <div className="font-semibold">
                                  ₹
                                  {parseFloat(
                                    item.total || item.price || "0"
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </div> */}
                                {/* {item.price && parseFloat(item.price) > 0 && (
                                  <div className="text-sm text-muted-foreground">
                                    ₹
                                    {parseFloat(item.price).toLocaleString(
                                      "en-IN",
                                      { minimumFractionDigits: 2 }
                                    )}{" "}
                                    each
                                  </div>
                                )} */}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted-foreground">No items found</p>
                      )}
                    </div>

                    {/* Right: stacked action buttons */}
                    <div className="w-full md:w-[260px] flex flex-col items-stretch gap-3">
                      {["processing", "completed"].includes(
                        (order.status || "").toLowerCase()
                      ) &&
                        order?.tracking_url && (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noreferrer"
                            className="w-full rounded-full bg-yellow-300 hover:bg-yellow-400 text-black font-semibold py-3 shadow text-center"
                          >
                            Track package
                          </a>
                        )}

                      {/* {order.status === "pending" && (
                        <button
                          onClick={() => navigate(`/order/${order.id}/cancel`)}
                          className="w-full rounded-full border border-gray-300 py-3 text-sm"
                        >
                          Request cancellation
                        </button>
                      )} */}

                      {/* <button
                        onClick={() => navigate(`/order/${order.id}/returns`)}
                        className="w-full rounded-full border border-gray-300 py-3 text-sm"
                      >
                        Return or replace items
                      </button> */}

                      {/* <button
                        onClick={() => navigate(`/order/${order.id}/gift`)}
                        className="w-full rounded-full border border-gray-300 py-3 text-sm"
                      >
                        Share gift receipt
                      </button> */}

                      {/* <button
                        onClick={() => navigate(`/order/${order.id}/review`)}
                        className="w-full rounded-full border border-gray-300 py-3 text-sm"
                      >
                        Write a product review
                      </button> */}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              Showing page {page} of {totalPages} — {totalItems} orders
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 rounded-md border disabled:opacity-50"
              >
                Prev
              </button>

              {/* show up to 5 page buttons centered on current page */}
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                // only render a window of pages
                if (
                  Math.abs(pageNum - page) > 2 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  if (pageNum === 2 && page > 4)
                    return (
                      <span key={pageNum} className="px-2">
                        ...
                      </span>
                    );
                  if (pageNum === totalPages - 1 && page < totalPages - 3)
                    return (
                      <span key={pageNum} className="px-2">
                        ...
                      </span>
                    );
                  return null;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded-md border ${
                      pageNum === page ? "bg-primary text-white" : ""
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-1 rounded-md border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
      <AppFooter />
    </div>
  );
};

export default Orders;